import privacySource from "./privacy.md?raw";
import termsSource from "./terms.md?raw";

/**
 * xmg-sec-opsdocs (P1). The privacy policy and terms of use are plain markdown files next to this module
 * (`privacy.md`, `terms.md`), so the owner can replace the text without touching code. Each starts with a front
 * matter block:
 *
 *     ---
 *     title: Privacy policy
 *     version: 2026-10-23-draft
 *     ---
 *
 * `version` is load-bearing: sign-up sends `terms:<version>,privacy:<version>` as `legal_version`, and the server
 * stores it on the user and in the audit trail as the record of WHICH text the user agreed to. Bump it whenever
 * the text changes. The server accepts letters, digits and `._+-` in a version.
 */
export interface LegalDocument {
  title: string;
  version: string;
  body: string;
}

const VERSION_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._+-]*$/;

export function parseLegalDocument(source: string): LegalDocument {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(source);
  const fields: Record<string, string> = {};
  if (match !== null) {
    for (const line of (match[1] ?? "").split(/\r?\n/)) {
      const separator = line.indexOf(":");
      if (separator > 0) {
        fields[line.slice(0, separator).trim()] = line.slice(separator + 1).trim();
      }
    }
  }
  const version = fields.version ?? "";
  if (match === null || !VERSION_PATTERN.test(version)) {
    throw new Error("legal document has no valid front matter `version` (letters, digits and ._+- only)");
  }
  return { title: fields.title ?? "", version, body: match[2] ?? "" };
}

export const LEGAL_DOCUMENTS = {
  terms: parseLegalDocument(termsSource),
  privacy: parseLegalDocument(privacySource),
} as const;

export type LegalDocumentKey = keyof typeof LEGAL_DOCUMENTS;

/** The `legal_version` value sign-up sends: both documents' versions, so a change to either is recorded. */
export function legalVersion(): string {
  return `terms:${LEGAL_DOCUMENTS.terms.version},privacy:${LEGAL_DOCUMENTS.privacy.version}`;
}
